import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import IdentityGateDialog from "../components/pay/IdentityGateDialog";
import AdresseRecuFiscal, { adresseRecuAProposer } from "../components/pay/AdresseRecuFiscal";
import AddressAutocomplete from "../components/shared/AddressAutocomplete";
import {
  Elements,
  ExpressCheckoutElement,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  Loader2,
  ShieldCheck,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  CreditCard,
  Lock,
  ChevronLeft,
} from "lucide-react";

import { PUBLIC_API as API } from "../lib/api";
import { chargerStripe } from "../lib/stripe";
import TeroumaLogo from "../components/ui/TeroumaLogo";
import { useI18n } from "../i18n";
import LanguageSwitcher from "../i18n/LanguageSwitcher";
import DeclarerDejaRegle, { EncartDejaRegle, PastilleSignale } from "../components/relances/DeclarerDejaRegle";

// 💸 Surcharge "Je couvre les frais Stripe" — doit matcher exactement le
// calcul backend (STRIPE_FEE_RATE + STRIPE_FEE_FIXED_CENTS dans
// payment_routes.py) pour que le total affiché à l'écran corresponde au
// montant prélevé sur la carte.
const STRIPE_FEE_RATE = 0.015;        // 1,5%
const STRIPE_FEE_FIXED = 0.25;        // 0,25 €
const computeSurcharge = (subtotal) =>
  Math.round((subtotal * STRIPE_FEE_RATE) * 100) / 100 + STRIPE_FEE_FIXED;

const formatEUR = (v) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

// Format avec 2 décimales pour les petits montants type surcharge frais.
const formatEURPrecise = (v) =>
  new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(Number(v || 0));

const formatDate = (iso, lang = "fr") => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

// Rendu du crash dans un composant fonction : la classe ErrorBoundary ne peut
// pas appeler useI18n (hook), lui si.
function PayBoundaryFallback() {
  const { t } = useI18n();
  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#F4F3EF" }}>
      <div className="bg-white rounded-3xl p-10 max-w-sm w-full text-center shadow-lg">
        <div className="h-14 w-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-7 w-7 text-red-400" />
        </div>
        <p className="font-semibold text-stone-900 mb-2">{t("pay.error_title")}</p>
        <p className="text-sm text-stone-500 mb-5">{t("pay.boundary_body")}</p>
        <button onClick={() => window.location.reload()}
          className="w-full py-3 rounded-xl text-white text-sm font-medium"
          style={{ background: "#0D1B33" }}>
          {t("pay.boundary_reload")}
        </button>
      </div>
    </div>
  );
}

class PayErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { crashed: false, msg: "" }; }
  static getDerivedStateFromError(err) { return { crashed: true, msg: err?.message || "Erreur inattendue" }; }
  render() {
    if (this.state.crashed) return <PayBoundaryFallback />;
    return this.props.children;
  }
}

function PayPageInner() {
  const { token } = useParams();
  const { t } = useI18n();
  // « J'ai déjà réglé » : écran ouvert depuis le lien de la page, ou
  // directement par le bouton de l'email de relance (/pay/:token?declare=1).
  const [searchParams] = useSearchParams();
  const [declareOpen, setDeclareOpen] = useState(() => searchParams.has("declare"));
  // Changement d'écran (paiement ↔ « J'ai déjà réglé ») : on repart du haut,
  // sinon le donateur arrive au milieu du nouvel écran, titre caché.
  useEffect(() => {
    try { window.scrollTo({ top: 0 }); } catch { /* jsdom / vieux navigateur */ }
  }, [declareOpen]);
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const [stripeData, setStripeData] = useState(null);
  const [success, setSuccess] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  // 💸 Cocher par défaut — pratique standard sur les plateformes de don
  // (GoFundMe, Leetchi, HelloAsso). Le donateur peut décocher s'il préfère
  // que la synagogue absorbe les frais Stripe (via on_behalf_of).
  // DÉCOCHÉ par défaut (demande Avi, 31/07/2026) : couvrir les frais est un
  // geste volontaire du donateur, pas un supplément glissé dans le panier.
  const [coverFees, setCoverFees] = useState(false);
  // 🔁 Prélèvement automatique des échéances suivantes (abonnement mensuel,
  // résiliable). OPT-IN explicite — jamais coché par défaut (transparence).
  const [autoCharge, setAutoCharge] = useState(false);
  // Choix « payer en tant que » AU PAIEMENT (particulier / entreprise).
  const [payAs, setPayAs] = useState("particulier");
  const [company, setCompany] = useState({ company_name: "", siren: "", civility: "", address: "", postal_code: "", city: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 12000);
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/pay/${token}`, { signal: controller.signal }),
          axios.get(`${API}/payments/config`, { signal: controller.signal }),
        ]);
        clearTimeout(timeoutId);
        if (!cancelled) {
          setData(r1.data);
          setConfig(r2.data);
          const init = {};
          // Ne pré-cocher QUE le don concerné par le lien de relance : tout
          // cocher donnait l'impression d'une facture globale imposée. Les
          // autres dons restent listés, décochés — le donateur choisit.
          // Repli (vieux lien sans primary_pledge_id) : le premier de la liste.
          const pledgesList = r1.data?.pledges || [];
          // Commande passée depuis une page de fête : toutes ses lignes sont
          // pré-cochées (le donateur vient de les choisir). Sinon, seul le don
          // concerné par le lien de relance l'est.
          const preselect = r1.data?.preselect_pledge_ids || [];
          const primary = r1.data?.primary_pledge_id
            || (pledgesList[0] && pledgesList[0].pledge_id);
          pledgesList.forEach((p) => {
            init[p.pledge_id] = preselect.length
              ? preselect.includes(p.pledge_id)
              : p.pledge_id === primary;
          });
          setSelected(init);
          // Pré-remplit la société si déjà connue + propose « société » par défaut.
          const _d = r1.data || {};
          if (_d.donor_company_name) {
            setCompany({
              company_name: _d.donor_company_name || "",
              siren: _d.donor_siren || "",
              civility: _d.donor_civility || "",
              // Adresse du SIÈGE : champ société dédié. PAS de repli sur l'adresse
              // perso (sinon le siège deviendrait identique à la perso par recopie).
              address: _d.donor_company_address || "",
              postal_code: _d.donor_company_postal_code || "",
              city: _d.donor_company_city || "",
            });
            setPayAs("entreprise");
          }
        }
      } catch (err) {
        if (!cancelled) {
          const isCancelled = err?.name === "CanceledError" || err?.name === "AbortError";
          setError({
            status: isCancelled ? 408 : (err?.response?.status),
            detail: isCancelled
              ? t("pay.error_timeout")
              : (err?.response?.data?.detail || err?.message || t("pay.error_load")),
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Stripe.js n'est plus chargé par les autres pages publiques : on le
  // télécharge ici dès que la clé publique est connue, pour qu'il soit prêt
  // quand le donateur clique « Payer » (lib/stripe.js). Rien en mode démo.
  useEffect(() => {
    const cle = config?.publishable_key;
    if (!cle || config?.demo_mode) return;
    chargerStripe(cle).catch(() => { /* retenté au moment de payer */ });
  }, [config]);

  const items = data?.pledges || [];

  const subtotal = useMemo(() => {
    let t = 0;
    items.forEach((p) => {
      if (!selected[p.pledge_id]) return;
      t += p.has_installments && p.next_installment_amount
        ? p.next_installment_amount
        : p.amount_due_total;
    });
    return t;
  }, [items, selected]);

  // 💸 Surcharge "Je couvre les frais Stripe". Calcul exact = 1,5% + 0,25 €.
  // La synagogue reçoit pile le don, le donateur paye un peu plus pour
  // couvrir les frais Stripe. Si décoché : synagogue absorbe via on_behalf_of.
  const surcharge = coverFees ? computeSurcharge(subtotal) : 0;
  const total = subtotal + surcharge;
  const selectedCount = items.filter((p) => selected[p.pledge_id]).length;

  const beginCheckout = async () => {
    setCreatingIntent(true);
    setError(null);
    try {
      const selections = items
        .filter((p) => selected[p.pledge_id])
        .map((p) => ({
          pledge_id: p.pledge_id,
          installment_id:
            p.has_installments && p.next_installment_id
              ? p.next_installment_id
              : null,
        }));
      const res = await axios.post(`${API}/payments/create-intent`, {
        token,
        selections,
        cover_fees: coverFees,
        paid_as: payAs,
        auto_charge_rest: autoCharge,
        ...(payAs === "entreprise" ? { company } : {}),
      });
      setStripeData(res.data);
    } catch (err) {
      setError({
        status: err?.response?.status,
        detail:
          err?.response?.data?.detail || t("pay.error_init"),
      });
    } finally {
      setCreatingIntent(false);
    }
  };

  // simulatePay supprimé — endpoint backend retiré pour sécurité.

  const refreshData = async () => {
    const r1 = await axios.get(`${API}/pay/${token}`);
    setData(r1.data);
  };

  // Paiement direct : lien issu d'un don fait sans code. Payer tout de suite
  // reste immédiat ; les actions qui engagent la fiche passent d'abord par
  // le code (IdentityGateDialog), puis reprennent toutes seules.
  const [actionApresCode, setActionApresCode] = useState(null);
  const avecIdentite = (action) => {
    if (data?.identity_verified === false) setActionApresCode(() => action);
    else action();
  };

  const onPaid = (info) => {
    setSuccess({
      total_paid: subtotal,
      first_name: data.donor_first_name,
      organization_name: data.organization_name,
      auto_charge: autoCharge,
      ...info,
    });
  };

  return (
    <div
      data-testid="pay-page"
      className="min-h-screen bg-chk-bg flex flex-col"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      {/* Centered header */}
      <header className="relative w-full px-5 sm:px-8 py-7 flex flex-col items-center gap-2">
        <div className="absolute right-4 sm:right-6 top-4">
          <LanguageSwitcher />
        </div>
        <TeroumaLogo size={40} textColor="#0D1B33" variant="wordmark" />
        <p className="text-[11px] uppercase tracking-[0.22em] text-chk-ash inline-flex items-center gap-1.5">
          <Lock className="h-3 w-3" />
          {t("pay.secure_badge")}
        </p>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 pb-12">
        {loading ? (
          <div className="py-24">
            <Loader2 className="h-7 w-7 animate-spin text-chk-navy" />
          </div>
        ) : success ? (
          <SuccessCard {...success} />
        ) : error ? (
          <ErrorCard
            detail={error.detail}
            retry={() => {
              setError(null);
              setStripeData(null);
            }}
          />
        ) : declareOpen && !data?.awaiting_confirmation && data?.identity_verified !== false ? (
          // Commande « à confirmer » : pas de « J'ai déjà réglé » (rien n'a
          // pu être réglé autrement) → la carte de paiement s'affiche.
          // Plus rien à régler : l'écran le dit (« Tout est réglé »).
          <DeclarerDejaRegle
            token={token}
            data={data}
            selection={items.filter((p) => selected[p.pledge_id]).map((p) => p.pledge_id)}
            onBack={() => setDeclareOpen(false)}
            onDeclared={refreshData}
          />
        ) : stripeData ? (
          <CheckoutStage
            stripeData={stripeData}
            amount={total}
            data={data}
            onPaid={onPaid}
            onBack={() => setStripeData(null)}
          />
        ) : (
          <PayCard
            data={data}
            items={items}
            selected={selected}
            setSelected={setSelected}
            subtotal={subtotal}
            total={total}
            surcharge={surcharge}
            coverFees={coverFees}
            setCoverFees={setCoverFees}
            autoCharge={autoCharge}
            setAutoCharge={setAutoCharge}
            payAs={payAs}
            setPayAs={setPayAs}
            company={company}
            setCompany={setCompany}
            beginCheckout={beginCheckout}
            creatingIntent={creatingIntent}
            selectedCount={selectedCount}
            config={config}
            token={token}
            refresh={refreshData}
            onDeclare={() => avecIdentite(() => setDeclareOpen(true))}
            avecIdentite={avecIdentite}
          />
        )}
        {actionApresCode && (
          <IdentityGateDialog
            token={token}
            data={data}
            onClose={() => setActionApresCode(null)}
            onVerified={async () => {
              const suite = actionApresCode;
              setActionApresCode(null);
              await refreshData();
              suite();
            }}
          />
        )}
      </main>

      <footer className="px-6 py-7 text-center">
        <div className="max-w-md mx-auto chk-card p-5 flex items-start gap-3 text-left">
          <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="text-xs text-chk-ash leading-relaxed">
            <strong className="text-chk-ink">{t("pay.footer_secure_title")}</strong>{" "}
            {t("pay.footer_secure_body")}
            {config?.demo_mode && (
              <span className="block mt-1 text-amber-700">
                {t("pay.demo_banner")}
              </span>
            )}
          </div>
        </div>
        <p className="text-[10px] uppercase tracking-[0.22em] text-stone-400 mt-5">
          {t("pay.motto")}
        </p>
      </footer>
    </div>
  );
}

export default function PayPage() {
  return (
    <PayErrorBoundary>
      <PayPageInner />
    </PayErrorBoundary>
  );
}

function PayCard({
  data,
  items,
  selected,
  setSelected,
  subtotal,
  total,
  surcharge,
  coverFees,
  setCoverFees,
  autoCharge,
  setAutoCharge,
  payAs,
  setPayAs,
  company,
  setCompany,
  beginCheckout,
  creatingIntent,
  selectedCount,
  config,
  refresh,
  token,
  onDeclare,
  avecIdentite = (action) => action(),
}) {
  const { t, lang } = useI18n();
  // « Je paierai plus tard » — commande publique fraîche, pas encore
  // confirmée : UN clic enregistre l'engagement (fin du filet 48 h) et le
  // lien de paiement part par email en mémo. Aucune autre action requise.
  const [laterState, setLaterState] = useState(""); // "" | saving | done | error
  const [laterEmailed, setLaterEmailed] = useState(false);
  const confirmLater = async () => {
    setLaterState("saving");
    try {
      const r = await axios.post(`${API}/pay/${token}/confirm-later`);
      setLaterEmailed(Boolean(r.data?.emailed));
      setLaterState("done");
      // Recharge les données : awaiting_confirmation retombe à false, donc
      // le bouton ne réapparaît pas après un aller-retour vers le paiement.
      try { await refresh(); } catch { /* non bloquant */ }
    } catch {
      setLaterState("error");
    }
  };
  // Échéance sélectionnée SANS prélèvement auto déjà actif → on propose
  // l'option « prélever automatiquement la suite » (abonnement).
  const autoCandidate = items.find(
    (p) =>
      selected[p.pledge_id] &&
      p.has_installments &&
      p.next_installment_id &&
      !p.auto_charge_active &&
      p.installments_total - p.installments_paid > 1
  );
  const cancelAutoCharge = async (pledgeId) => {
    try {
      await axios.post(`${API}/payments/recurring/cancel`, {
        token,
        pledge_id: pledgeId,
      });
      await refresh();
    } catch (e) {
      // silencieux — le donateur peut réessayer
    }
  };
  const payAsInvalid =
    payAs === "entreprise" &&
    (!company?.company_name?.trim() ||
      !company?.address?.trim() || !company?.postal_code?.trim() || !company?.city?.trim());
  const coInputCls =
    "w-full px-3 py-2.5 rounded-xl border border-white/15 bg-white/5 text-white placeholder:text-white/40 text-sm focus:outline-none focus:border-chk-gold";
  return (
    <div className="w-full max-w-2xl chk-fade-up">
      <div className="text-center mb-8 sm:mb-10">
        <p className="text-[11px] uppercase tracking-[0.22em] text-chk-ash mb-3">
          {t("pay.personal_link")}
        </p>
        <h1 className="font-serif text-4xl sm:text-5xl text-chk-navy tracking-tight mb-3 leading-[1.05]">
          {t("pay.hello", { name: data.donor_first_name })}
        </h1>
        <p className="text-chk-ash leading-relaxed max-w-md mx-auto text-sm sm:text-base">
          {t("pay.thanks_prefix")}
          <span className="font-medium text-chk-ink">
            {data.organization_name}
          </span>
          {t("pay.thanks_suffix")}
        </p>
      </div>

      <div className="chk-card p-2">
        <div className="px-4 sm:px-5 py-3 flex items-center justify-between border-b border-stone-100 flex-wrap gap-2">
          <p className="text-[11px] uppercase tracking-[0.2em] text-chk-ash font-semibold">
            {t("pay.pending_title")}
          </p>
          <p className="text-[11px] text-stone-400 inline-flex items-center gap-1.5">
            <Calendar className="h-3 w-3" />
            {t("pay.expires", { date: formatDate(data.expires_at, lang) })}
          </p>
        </div>
        <ul>
          {items.map((p) => {
            const isInstallment =
              p.has_installments && p.next_installment_amount;
            const payNow = isInstallment
              ? p.next_installment_amount
              : p.amount_due_total;
            const isSelected = Boolean(selected[p.pledge_id]);
            // Le choix du rythme est offert TANT QUE rien n'est payé — y
            // compris pour un engagement déjà en échéances (Beraha 12×) :
            // « tout régler en 1 fois » ou changer le nombre de mensualités.
            // Un prélèvement automatique actif fige le rythme (résilier d'abord).
            const canSplit = p.amount_paid === 0 && !p.auto_charge_active;
            return (
              <li key={p.pledge_id}>
                <label
                  data-testid={`pledge-option-${p.pledge_id}`}
                  className={`flex items-start gap-3 sm:gap-4 px-4 sm:px-5 py-4 cursor-pointer border-b border-stone-100 last:border-b-0 transition-colors ${
                    isSelected ? "bg-chk-gold/5" : "hover:bg-chk-bg"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) =>
                      setSelected({
                        ...selected,
                        [p.pledge_id]: e.target.checked,
                      })
                    }
                    data-testid={`pledge-checkbox-${p.pledge_id}`}
                    className="mt-1 h-5 w-5 rounded-md border-stone-300 cursor-pointer"
                    style={{ accentColor: "#1B2A4A" }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-chk-ink">
                      {p.motive}
                    </p>
                    {/* « J'ai déjà réglé » déjà signalé pour CETTE promesse */}
                    <PastilleSignale pledge={p} />
                    {p.description && (
                      <p className="text-sm text-chk-ink/70 mt-0.5">{p.description}</p>
                    )}
                    {p.donation_date && (
                      <p className="text-xs text-chk-ash mt-0.5">{t("pay.donation_date", { date: formatDate(p.donation_date, lang) })}</p>
                    )}
                    {isInstallment ? (
                      <p className="text-xs text-chk-ash mt-1">
                        {t("pay.installment_line", {
                          seq: p.next_installment_sequence,
                          total: p.installments_total,
                          date: formatDate(p.next_installment_due_date, lang),
                        })}
                      </p>
                    ) : (
                      <p className="text-xs text-chk-ash mt-1">{t("pay.one_time")}</p>
                    )}
                    {p.auto_charge_active && (
                      <div className="mt-2 inline-flex flex-wrap items-center gap-2 text-xs rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 px-2.5 py-1.5">
                        <span>{p.auto_charge_next_date
                          ? t("pay.auto_active_next", { date: formatDate(p.auto_charge_next_date, lang) })
                          : t("pay.auto_active")}</span>
                        <button
                          type="button"
                          onClick={(e) => { e.preventDefault(); cancelAutoCharge(p.pledge_id); }}
                          data-testid={`stop-auto-${p.pledge_id}`}
                          className="underline text-emerald-700 hover:text-rose-600"
                        >
                          {t("pay.stop")}
                        </button>
                      </div>
                    )}
                    {canSplit && (
                      <SplitSelector
                        pledgeId={p.pledge_id}
                        amount={p.total_amount ?? p.amount_due_total}
                        token={token}
                        hasInstallments={Boolean(p.has_installments)}
                        onSplit={refresh}
                        avecIdentite={avecIdentite}
                      />
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-serif text-2xl sm:text-3xl text-chk-navy tabular-nums">
                      {formatEUR(payNow)}
                    </p>
                    {isInstallment && (
                      <p className="text-[11px] text-stone-400 mt-0.5">
                        {t("pay.of_total", { amount: formatEUR(p.total_amount) })}
                      </p>
                    )}
                  </div>
                </label>
              </li>
            );
          })}
        </ul>
      </div>

      {/* 🤝 « J'ai déjà réglé » (espèces, chèque, virement remis à la
          synagogue) : le donateur SIGNALE, le gabbaï vérifie et décide.
          Masqué pour une commande publique toute fraîche (pas encore
          confirmée : rien n'a pu être réglé autrement). */}
      {items.length > 0 && !data.awaiting_confirmation && (
        <EncartDejaRegle data={data} onOpen={onDeclare} />
      )}

      {/* 🕗 « Je paierai plus tard » — visible tant que la commande n'est ni
          payée ni confirmée. Après le clic : confirmation immédiate sur la
          page, l'email n'est qu'un mémo (rien à cliquer dedans). */}
      {laterState === "done" ? (
        <div className="mt-5 p-4 rounded-2xl border border-emerald-300 bg-emerald-50 flex items-start gap-3"
          data-testid="pay-later-done">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-emerald-800">
              {t("pay.later_done_title")}
            </p>
            <p className="text-xs text-emerald-700 mt-1 leading-relaxed">
              {laterEmailed ? t("pay.later_done_body") : t("pay.later_done_body_noemail")}
            </p>
          </div>
        </div>
      ) : data.awaiting_confirmation ? (
        <div className="mt-5 p-4 rounded-2xl border border-stone-200 bg-white">
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-chk-gold mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-chk-ink">{t("pay.later_title")}</p>
              <p className="text-xs text-chk-ash mt-1 leading-relaxed">{t("pay.later_hint")}</p>
              {laterState === "error" && (
                <p className="text-xs text-red-600 mt-1">{t("pay.later_error")}</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => avecIdentite(confirmLater)}
              disabled={laterState === "saving"}
              data-testid="pay-later-button"
              className="shrink-0 px-4 py-2 rounded-full border border-chk-navy text-chk-navy text-xs font-semibold hover:bg-chk-navy hover:text-white transition-colors disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {laterState === "saving" && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {t("pay.later_button")}
            </button>
          </div>
        </div>
      ) : null}

      {/* 🧾 Lien VÉRIFIÉ sans adresse complète sur la fiche : « Adresse pour
          votre reçu fiscal » (facultatif, ne bloque jamais le paiement). */}
      {payAs !== "entreprise" && adresseRecuAProposer(data) && (
        <AdresseRecuFiscal token={token} data={data} onEnregistre={refresh} />
      )}

      {/* 💸 Couvrir les frais Stripe — case cochée par défaut */}
      <label
        htmlFor="cover-fees-toggle"
        className="mt-5 flex items-start gap-3 p-4 rounded-2xl border border-stone-200 bg-white hover:bg-stone-50 cursor-pointer transition-colors"
      >
        <input
          id="cover-fees-toggle"
          type="checkbox"
          checked={coverFees}
          onChange={(e) => setCoverFees(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-stone-300 text-chk-navy focus:ring-chk-navy/30 cursor-pointer"
          data-testid="cover-fees-checkbox"
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-chk-ink">
            {t("pay.cover_fees_label")}
            {subtotal > 0 && (
              <span className="text-chk-ash font-normal">
                {" "}— {formatEURPrecise(surcharge)}
              </span>
            )}
          </p>
          <p className="text-xs text-chk-ash mt-1 leading-relaxed">
            {t("pay.cover_fees_hint")}
          </p>
        </div>
      </label>

      {/* Total + CTA */}
      <div
        className="mt-6 rounded-3xl p-6 sm:p-7 text-white"
        style={{
          background:
            "linear-gradient(135deg, #1B2A4A 0%, #243456 100%)",
          boxShadow: "0 10px 30px rgba(27, 42, 74, 0.3)",
        }}
      >
        {/* Détail du calcul si frais couverts */}
        {coverFees && subtotal > 0 && (
          <div className="flex items-center justify-between text-xs text-white/70 mb-2">
            <span>{t("pay.line_donation")}</span>
            <span className="tabular-nums">{formatEUR(subtotal)}</span>
          </div>
        )}
        {coverFees && surcharge > 0 && (
          <div className="flex items-center justify-between text-xs text-white/70 mb-3">
            <span>{t("pay.line_fees")}</span>
            <span className="tabular-nums">+ {formatEURPrecise(surcharge)}</span>
          </div>
        )}
        <div className={`flex items-center justify-between ${coverFees ? "border-t border-white/10 pt-3" : "border-t border-white/10 pt-4"} mb-5`}>
          <p className="text-sm font-semibold tracking-[0.1em] uppercase text-white">
            {t("pay.total_due")}
          </p>
          <p
            className="font-serif text-4xl sm:text-5xl tabular-nums text-chk-gold"
            data-testid="pay-total"
          >
            {coverFees && total > 0 ? formatEURPrecise(total) : formatEUR(total)}
          </p>
        </div>
        {/* Vous payez en tant que : particulier / société (choix AU paiement) */}
        <div className="rounded-2xl border border-white/10 p-4 mb-4">
          <p className="text-xs uppercase tracking-wide text-white/60 mb-2">{t("pay.pay_as")}</p>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => setPayAs("particulier")}
              className={`px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-colors ${payAs !== "entreprise" ? "border-chk-gold bg-chk-gold/15 text-white" : "border-white/15 text-white/70 hover:bg-white/5"}`}>
              {t("pay.pay_as_self")}<span className="block text-[11px] font-normal opacity-70">{t("pay.pay_as_self_hint")}</span>
            </button>
            <button type="button" onClick={() => avecIdentite(() => setPayAs("entreprise"))}
              className={`px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-colors ${payAs === "entreprise" ? "border-chk-gold bg-chk-gold/15 text-white" : "border-white/15 text-white/70 hover:bg-white/5"}`}>
              {t("pay.pay_as_company")}<span className="block text-[11px] font-normal opacity-70">{t("pay.pay_as_company_hint")}</span>
            </button>
          </div>
          {payAs === "entreprise" && (
            <div className="mt-3 space-y-2">
              <input value={company.company_name} onChange={(e) => setCompany((c) => ({ ...c, company_name: e.target.value }))} placeholder={`${t("common.company_name")} *`} className={coInputCls} />
              <div className="grid grid-cols-2 gap-2">
                <input value={company.siren} onChange={(e) => setCompany((c) => ({ ...c, siren: e.target.value }))} placeholder={t("common.siren")} className={coInputCls} />
                <input value={company.civility} onChange={(e) => setCompany((c) => ({ ...c, civility: e.target.value }))} placeholder={t("pay.civility_placeholder")} className={coInputCls} />
              </div>
              <AddressAutocomplete value={company.address} onChange={(v) => setCompany((c) => ({ ...c, address: v }))} onSelect={({ address, postalCode, city }) => setCompany((c) => ({ ...c, address, postal_code: postalCode, city }))} placeholder={t("pay.company_address_placeholder")} inputClassName={coInputCls} />
              <div className="grid grid-cols-2 gap-2">
                <input value={company.postal_code} onChange={(e) => setCompany((c) => ({ ...c, postal_code: e.target.value }))} placeholder={`${t("common.postal_code")} *`} className={coInputCls} />
                <input value={company.city} onChange={(e) => setCompany((c) => ({ ...c, city: e.target.value }))} placeholder={`${t("common.city")} *`} className={coInputCls} />
              </div>
              <p className="text-[11px] text-white/50">{t("pay.company_cerfa_note")}</p>
            </div>
          )}
        </div>
        {/* 🔁 Abonnement : prélèvement automatique des échéances suivantes */}
        {autoCandidate && (
          <label className="flex items-start gap-3 rounded-2xl border p-4 mb-4 cursor-pointer transition-colors"
            style={{ borderColor: autoCharge ? "var(--trm-gold, #B8962E)" : "rgba(255,255,255,0.15)", background: autoCharge ? "rgba(184,150,46,0.12)" : "transparent" }}
            data-testid="auto-charge-option">
            <input
              type="checkbox"
              checked={autoCharge}
              onChange={(e) => {
                const coche = e.target.checked;
                if (coche) avecIdentite(() => setAutoCharge(true));
                else setAutoCharge(false);
              }}
              className="mt-0.5 h-5 w-5 rounded-md cursor-pointer"
              style={{ accentColor: "#B8962E" }}
              data-testid="auto-charge-checkbox"
            />
            <span className="text-sm text-white/90">
              <strong>{t("pay.auto_title")}</strong>
              <span className="block text-[12px] text-white/60 mt-1 leading-relaxed">
                {t("pay.auto_body_1")}
                <strong>{t("pay.auto_body_sub")}</strong>
                {t("pay.auto_body_2")}
                <strong>{t("pay.auto_body_amount", { amount: formatEUR(autoCandidate.next_installment_amount || 0) })}</strong>
                {t("pay.auto_body_3", {
                  count: Math.max(autoCandidate.installments_total - autoCandidate.installments_paid - 1, 1),
                })}
                <strong>{t("pay.auto_body_cancel")}</strong>
                {t("pay.auto_body_4")}
              </span>
            </span>
          </label>
        )}

        <button
          onClick={beginCheckout}
          disabled={creatingIntent || selectedCount === 0 || payAsInvalid}
          data-testid="pay-now-button"
          className="w-full h-12 inline-flex items-center justify-center gap-2 rounded-full bg-chk-gold text-chk-navy font-medium text-sm hover:bg-chk-gold2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {creatingIntent ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <CreditCard className="h-4 w-4" />
          )}
          {selectedCount === 0
            ? t("pay.select_one")
            : t("pay.continue")}
        </button>
      </div>
    </div>
  );
}

function CheckoutStage({ stripeData, amount, data, onPaid, onBack }) {
  if (stripeData.demo_mode) {
    return (
      <DemoCheckout
        stripeData={stripeData}
        amount={amount}
        data={data}
        onPaid={onPaid}
        onBack={onBack}
      />
    );
  }
  return (
    <StripeCheckout
      stripeData={stripeData}
      amount={amount}
      data={data}
      onPaid={onPaid}
      onBack={onBack}
    />
  );
}

function StripeCheckout({ stripeData, amount, data, onPaid, onBack }) {
  const stripePromise = useMemo(
    () => chargerStripe(stripeData.publishable_key),
    [stripeData.publishable_key]
  );
  return (
    <div className="w-full max-w-md chk-fade-up">
      <CheckoutHeader amount={amount} org={data.organization_name} onBack={onBack} />
      <div className="chk-card p-5 sm:p-6">
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: stripeData.client_secret,
            appearance: {
              theme: "stripe",
              variables: {
                colorPrimary: "#1B2A4A",
                colorBackground: "#ffffff",
                colorText: "#0E1726",
                fontFamily: "Manrope, sans-serif",
                borderRadius: "12px",
              },
            },
          }}
        >
          <StripePaymentForm onPaid={onPaid} amount={amount} />
        </Elements>
      </div>
    </div>
  );
}

function StripePaymentForm({ onPaid, amount }) {
  const { t } = useI18n();
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Apple Pay / Google Pay disponibles sur cet appareil → gros bouton en
  // haut (un geste, rien à taper), la carte reste proposée en dessous.
  const [portefeuille, setPortefeuille] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await payer();
  };

  const payer = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const { error: err, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });
    if (err) {
      setError(err.message || t("pay.payment_failed"));
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      // Sync settlement immediately so the donor sees pledge=paid + Cerfa
      // even when the Stripe webhook is delayed or blocked.
      try {
        await axios.post(`${API}/payments/confirm-intent`, {
          payment_intent_id: paymentIntent.id,
        });
      } catch (e) {
        // Webhook will catch up later; don't block donor success UI
        console.warn("confirm-intent fallback failed:", e?.response?.data);
      }
      onPaid({ payment_intent_id: paymentIntent.id });
    } else {
      setError(t("pay.payment_not_confirmed"));
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <ExpressCheckoutElement
        options={{
          buttonType: { applePay: "donate", googlePay: "donate" },
          buttonHeight: 52,
          // Seulement les portefeuilles sans redirection (pas de Link, PayPal…).
          paymentMethods: { applePay: "auto", googlePay: "auto", link: "never", paypal: "never", amazonPay: "never", klarna: "never" },
        }}
        onReady={({ availablePaymentMethods }) =>
          setPortefeuille(Boolean(availablePaymentMethods && Object.values(availablePaymentMethods).some(Boolean)))
        }
        onConfirm={payer}
      />
      {portefeuille && (
        <div className="flex items-center gap-3 text-xs text-chk-ash" aria-hidden="true">
          <span className="h-px flex-1 bg-stone-200" />
          ou par carte
          <span className="h-px flex-1 bg-stone-200" />
        </div>
      )}
      <PaymentElement options={{ layout: "tabs", wallets: { applePay: "never", googlePay: "never" } }} />
      {error && (
        <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="stripe-pay-submit"
        className="h-12 inline-flex items-center justify-center gap-2 rounded-full bg-chk-navy text-white hover:bg-chk-navy2 transition-all text-sm font-medium disabled:opacity-50"
      >
        {submitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        {t("pay.pay_amount", { amount: formatEUR(amount) })}
      </button>
    </form>
  );
}

function DemoCheckout({ stripeData, amount, data, onPaid, onBack }) {
  const { t } = useI18n();
  const [cardNumber, setCardNumber] = useState("4242 4242 4242 4242");
  const [exp, setExp] = useState("12/30");
  const [cvc, setCvc] = useState("123");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/payments/demo-confirm`, {
        payment_intent_id: stripeData.payment_intent_id,
        succeed: true,
      });
      onPaid({
        payment_intent_id: stripeData.payment_intent_id,
        demo_mode: true,
      });
    } catch (err) {
      setError(err?.response?.data?.detail || t("pay.demo_error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-md chk-fade-up">
      <CheckoutHeader amount={amount} org={data.organization_name} onBack={onBack} />
      <form
        onSubmit={submit}
        data-testid="demo-checkout-form"
        className="chk-card p-5 sm:p-6 flex flex-col gap-5"
      >
        <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          {t("pay.demo_hint")}
        </div>

        <div className="flex flex-col">
          <label className="chk-label mb-1.5 inline-flex items-center gap-1.5">
            <CreditCard className="h-3 w-3" />
            {t("pay.card_number")}
          </label>
          <input
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
            data-testid="demo-card-number"
            className="chk-input h-12 font-mono tracking-wider"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col">
            <label className="chk-label mb-1.5">{t("pay.card_expiry")}</label>
            <input
              value={exp}
              onChange={(e) => setExp(e.target.value)}
              data-testid="demo-card-exp"
              className="chk-input h-12 font-mono"
            />
          </div>
          <div className="flex flex-col">
            <label className="chk-label mb-1.5">CVC</label>
            <input
              value={cvc}
              onChange={(e) => setCvc(e.target.value)}
              data-testid="demo-card-cvc"
              className="chk-input h-12 font-mono"
            />
          </div>
        </div>

        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          data-testid="demo-pay-submit"
          className="h-12 inline-flex items-center justify-center gap-2 rounded-full bg-chk-navy text-white hover:bg-chk-navy2 transition-all text-sm font-medium disabled:opacity-50"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          {t("pay.pay_amount", { amount: formatEUR(amount) })}
        </button>
      </form>
    </div>
  );
}

function CheckoutHeader({ amount, org, onBack }) {
  const { t } = useI18n();
  return (
    <div className="mb-6 text-center">
      <button
        onClick={onBack}
        className="text-xs text-chk-ash hover:text-chk-ink mb-3 inline-flex items-center gap-1"
      >
        <ChevronLeft className="h-3 w-3" /> {t("pay.edit_selection")}
      </button>
      <p className="text-[11px] uppercase tracking-[0.22em] text-chk-ash mb-2">
        {t("pay.payment_to", { org })}
      </p>
      <p className="font-serif text-5xl text-chk-navy tabular-nums">
        {formatEUR(amount)}
      </p>
    </div>
  );
}

function SplitSelector({ pledgeId, amount, token, hasInstallments = false, onSplit,
                         avecIdentite = (action) => action() }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  // Engagement déjà mensualisé (Beraha) → le choix mis en avant est
  // « tout régler en 1 fois » ; sinon, étalement classique en 3×.
  const [n, setN] = useState(hasInstallments ? 1 : 3);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/pay/${token}/installments`, {
        pledge_id: pledgeId,
        installments: n,
      });
      await onSplit();
    } catch (e) {
      // silent — keep simple
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          avecIdentite(() => setOpen(true));
        }}
        data-testid={`split-toggle-${pledgeId}`}
        className="mt-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] text-chk-gold hover:text-chk-navy transition-colors"
      >
        <Calendar className="h-3 w-3" />
        {hasInstallments ? t("pay.split_toggle_change") : t("pay.split_toggle")}
      </button>
    );
  }
  return (
    <div
      onClick={(e) => e.preventDefault()}
      className="mt-3 rounded-xl bg-chk-bg border border-stone-200 p-3"
    >
      <p className="text-[11px] uppercase tracking-[0.15em] text-chk-ash mb-2">
        {t("pay.split_title", { amount: formatEUR(amount) })}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {[1, 2, 3, 4, 6, 12].map((opt) => (
          <button
            type="button"
            key={opt}
            data-testid={`split-${pledgeId}-${opt}`}
            onClick={(e) => {
              e.preventDefault();
              setN(opt);
            }}
            className={`px-2.5 py-1 rounded-full text-xs transition-colors ${
              n === opt
                ? "bg-chk-navy text-white"
                : "bg-white text-chk-ash hover:text-chk-ink border border-stone-200"
            }`}
          >
            {opt}×
          </button>
        ))}
      </div>
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-chk-ash">
          {n === 1
            ? t("pay.split_once", { amount: formatEUR(amount) })
            : t("pay.split_per_month", { n, amount: formatEUR(amount / n) })}
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setOpen(false);
            }}
            className="text-[11px] text-chk-ash hover:text-chk-ink px-2 py-1"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={apply}
            data-testid={`split-apply-${pledgeId}`}
            className="text-[11px] bg-chk-navy text-white px-3 py-1 rounded-full hover:bg-chk-navy2 disabled:opacity-50"
          >
            {busy ? "…" : t("common.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function SuccessCard({ total_paid, first_name, organization_name, auto_charge }) {
  const { t } = useI18n();
  return (
    <div
      data-testid="pay-success"
      className="max-w-md w-full chk-card p-8 sm:p-10 text-center chk-fade-up"
    >
      <div className="h-14 w-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-6">
        <CheckCircle2 className="h-7 w-7 text-emerald-700" />
      </div>
      <h2 className="font-serif text-3xl sm:text-4xl text-chk-navy tracking-tight mb-3">
        {t("pay.success_title", { name: first_name })}
      </h2>
      <p className="text-sm text-chk-ash mb-6 leading-relaxed">
        {t("pay.success_1")}
        <span className="font-semibold text-chk-ink">
          {formatEUR(total_paid)}
        </span>
        {t("pay.success_2")}<span className="font-medium">{organization_name}</span>
        {t("pay.success_3")}
        <br />
        <span className="text-stone-500 text-xs">
          {t("pay.success_receipt")}
        </span>
      </p>
      {auto_charge && (
        <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-left text-xs text-amber-900 leading-relaxed" data-testid="auto-charge-confirmed">
          🔁 <strong>{t("pay.auto_on_title")}</strong>
          {t("pay.auto_on_body")}
        </div>
      )}
      <p className="text-[11px] text-stone-400 uppercase tracking-[0.22em]">
        {t("pay.motto")}
      </p>
    </div>
  );
}

function ErrorCard({ detail, retry }) {
  const { t } = useI18n();
  return (
    <div
      data-testid="pay-error"
      className="max-w-md w-full chk-card p-8 sm:p-10 text-center chk-fade-up"
    >
      <div className="h-14 w-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-6">
        <AlertTriangle className="h-7 w-7 text-rose-700" />
      </div>
      <h2 className="font-serif text-3xl text-chk-navy tracking-tight mb-3">
        {t("pay.error_title")}
      </h2>
      <p className="text-sm text-chk-ash leading-relaxed mb-6">
        {detail || t("pay.error_fallback")}
      </p>
      {retry && (
        <button
          onClick={retry}
          data-testid="pay-retry"
          className="chk-btn-primary"
        >
          {t("common.retry")}
        </button>
      )}
    </div>
  );
}

